import { useEffect, useState } from "react"
import { BracketClient } from "@/domain/bracket/client/BracketClient"
import { TournamentClient } from "@/domain/tournament/client/TournamentClient"
import type {
  BracketDetail,
  BracketMatchOrderItem,
  BracketSummaryItem,
} from "@/domain/bracket/model/Bracket"
import type { TournamentListItem } from "@/domain/tournament/model/Tournament"

/** 화면 모드 — 체급별 트리 / 대회 전체 대진표 / 대회 경기 순서 */
type BracketViewMode = "TREE" | "ALL" | "ORDER"

/**
 * 공개 대진표 훅.
 *
 * 대회를 고르면 그 대회의 체급 목록과 경기 순서를 함께 받는다.
 * 체급을 고르면 그 체급의 경기 트리를 받는다.
 * 대회 목록이 필요해 대회 클라이언트도 함께 쓴다 (화면이 두 도메인을 직접 조립하지 않게).
 *
 * 편성한 대진표가 곧 공개 대진표라 화면이 상태로 다시 거르지 않는다.
 */
export function useBracket() {
  const [tournaments, setTournaments] = useState<TournamentListItem[]>([])
  const [tournamentId, setTournamentId] = useState("")
  const [summaries, setSummaries] = useState<BracketSummaryItem[]>([])
  const [matchOrders, setMatchOrders] = useState<BracketMatchOrderItem[]>([])
  const [allBrackets, setAllBrackets] = useState<BracketDetail[]>([])
  const [detail, setDetail] = useState<BracketDetail | null>(null)
  const [viewMode, setViewMode] = useState<BracketViewMode>("TREE")
  const [isLoaded, setIsLoaded] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  function toErrorMessage(
    error: unknown,
    fallbackMessage: string,
  ): string {
    return error instanceof Error ? error.message : fallbackMessage
  }

  async function fetchTournaments() {
    try {
      const items = await TournamentClient.getByPaging()
      setTournaments(items)
      setErrorMessage("")

      return items
    } catch (error) {
      setTournaments([])
      setErrorMessage(toErrorMessage(error, "대회 목록을 불러오지 못했습니다."))

      return []
    }
  }

  async function fetchBrackets(nextTournamentId: string) {
    setTournamentId(nextTournamentId)
    setDetail(null)

    setAllBrackets([])

    if (!nextTournamentId) {
      setSummaries([])
      setMatchOrders([])
      return
    }

    try {
      const [loadedSummaries, loadedOrders] = await Promise.all([
        BracketClient.getByTournament(nextTournamentId),
        BracketClient.getMatchOrder(nextTournamentId),
      ])
      setSummaries(loadedSummaries)
      setMatchOrders(loadedOrders)
      setErrorMessage("")
    } catch (error) {
      setSummaries([])
      setMatchOrders([])
      setErrorMessage(toErrorMessage(error, "대진표를 불러오지 못했습니다."))
    }
  }

  async function openDetail(bracketId: string) {
    try {
      const loaded = await BracketClient.getById(bracketId)
      setDetail(loaded)
      setViewMode("TREE")
      setErrorMessage("")
    } catch (error) {
      setDetail(null)
      setErrorMessage(toErrorMessage(error, "대진표를 불러오지 못했습니다."))
    }
  }

  function closeDetail() {
    setDetail(null)
  }

  /**
   * 대회 전체 대진표 (모든 체급의 트리)
   *
   * 체급마다 다시 조회하지 않는다 — 서버가 경기 목록까지 한 번에 내려준다.
   * 탭을 눌렀을 때만 읽어 온다(양이 있어 대회를 고를 때 미리 받지 않는다).
   */
  async function fetchAllBrackets(targetTournamentId: string) {
    try {
      const loaded = await BracketClient.getAllByTournament(targetTournamentId)
      setAllBrackets(loaded)
      setErrorMessage("")
    } catch (error) {
      setAllBrackets([])
      setErrorMessage(toErrorMessage(error, "전체 대진표를 불러오지 못했습니다."))
    }
  }

  async function selectViewMode(nextViewMode: BracketViewMode) {
    setViewMode(nextViewMode)
    setDetail(null)

    const needsAllBrackets = nextViewMode === "ALL" && allBrackets.length === 0
    if (needsAllBrackets && tournamentId) await fetchAllBrackets(tournamentId)
  }

  useEffect(() => {
    async function initialize() {
      const items = await fetchTournaments()
      const featured =
        items.find((tournament) => tournament.isEntryOpen) ?? items[0]

      if (featured) await fetchBrackets(featured.id)

      setIsLoaded(true)
    }

    void initialize()
  }, [])

  return {
    tournaments,
    tournamentId,
    summaries,
    matchOrders,
    allBrackets,
    detail,
    viewMode,
    isLoaded,
    errorMessage,
    selectTournament: fetchBrackets,
    selectViewMode,
    openDetail,
    closeDetail,
  }
}

export type { BracketViewMode }
